import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import PreferenceTable from '../components/PreferenceTable';
import { useTablePreferences } from '../hooks/useTablePreferences';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  FileText, 
  Trash2, 
  Edit, 
  ArrowRight, 
  Loader2, 
  Printer, 
  Search,
  LayoutGrid,
  TableIcon,
  DollarSign,
  Copy,
  Bell,
  MessageSquareText,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getStatusStyle, getStatusLabel } from '../lib/proposalUtils';
import ProposalFormDialog from '../components/ProposalFormDialog';
import ProposalPreviewDialog from '../components/ProposalPreviewDialog';
import ProposalFollowUpDialog from '../components/proposal/ProposalFollowUpDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Proposal statuses with colors. Five statuses total: Draft, Sent,
// Accepted, Completed, Lost. "Completed" (Sep-2026) is a manual close-out
// from Accepted only — status-only change, no auto-archive; a proposal
// moves out of the active list only via the separate manual Archive
// action (single-row or bulk), same as Lost and Accepted. "Lost" was
// renamed from "Rejected" (Aug-2026) — straight rename, stored value
// changed too (see server-side migration).
const PROPOSAL_STATUSES = [
  { value: 'Draft', label: 'Draft', color: 'bg-slate-100 text-slate-800' },
  { value: 'Sent', label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  { value: 'Accepted', label: 'Accepted', color: 'bg-green-100 text-green-800' },
  { value: 'Completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'Lost', label: 'Lost', color: 'bg-red-100 text-red-800' },
];

export default function Proposals() {
  const { isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [followUpProposal, setFollowUpProposal] = useState(null);
  
  // Search and filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // View mode: 'table' or 'cards' - default to table, persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('proposalsViewMode') || 'table';
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // "Lost" reason popup — shown when a proposal's status is changed to
  // Lost. Lightweight and skippable: a short text box, not a form.
  const [lostReasonTarget, setLostReasonTarget] = useState(null);
  const [lostReasonText, setLostReasonText] = useState('');
  const [savingLostReason, setSavingLostReason] = useState(false);

  // Bulk selection — checkboxes render only on Accepted/Completed/Lost
  // rows (terminal statuses). Toolbar above the table offers the action
  // that matches the CURRENT selection's status (mixed = disabled).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Permission checks
  const canViewProposals = isAdmin() || hasPermission('view_proposals') || hasPermission('create_proposals');
  const canCreateProposals = isAdmin() || hasPermission('create_proposals');

  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('proposalsViewMode', viewMode);
  }, [viewMode]);

  // Redirect unauthorized users
  useEffect(() => {
    if (!canViewProposals) {
      toast.error('Access denied. You do not have permission to view proposals.');
      navigate('/');
    }
  }, [canViewProposals, navigate]);

  // Handle ?new=true URL param to open create dialog
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      const companyId = searchParams.get('companyId');
      const companyName = searchParams.get('company');
      const contactName = searchParams.get('contact');
      const email = searchParams.get('email');
      const phone = searchParams.get('phone');
      const street = searchParams.get('street');
      const city = searchParams.get('city');
      const state = searchParams.get('state');
      const zip = searchParams.get('zip');
      const apEmail = searchParams.get('apEmail');
      
      if (companyName) {
        setEditData({
          prefill_company_id: companyId ? decodeURIComponent(companyId) : '',
          client_name: decodeURIComponent(companyName),
          contact_name: contactName ? decodeURIComponent(contactName) : '',
          client_email: email ? decodeURIComponent(email) : '',
          client_phone: phone ? decodeURIComponent(phone) : '',
          client_street: street ? decodeURIComponent(street) : '',
          client_city: city ? decodeURIComponent(city) : '',
          client_state: state ? decodeURIComponent(state) : '',
          client_zip: zip ? decodeURIComponent(zip) : '',
          ar_email_address: apEmail ? decodeURIComponent(apEmail) : '',
        });
      } else {
        setEditData(null);
      }
      setShowForm(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const loadProposals = useCallback(async () => {
    try {
      const res = await axios.get(API + '/proposals');
      setProposals(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPreviewData = async () => {
    if (previewData) {
      try {
        const res = await axios.get(`${API}/proposals/${previewData.id}`);
        setPreviewData(res.data);
        loadProposals();
      } catch (e) {
        console.error('Failed to refresh proposal:', e);
      }
    }
  };

  const loadContacts = useCallback(async () => {
    try {
      const res = await axios.get(API + '/contacts');
      setContacts(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadProposals();
    loadContacts();
  }, [loadProposals, loadContacts]);

  // Filter and sort proposals
  const matchesSearchTerm = (p, term) =>
    p.proposal_number?.toLowerCase().includes(term) ||
    p.client_name?.toLowerCase().includes(term) ||
    p.proposal_type?.toLowerCase().includes(term) ||
    p.contact_name?.toLowerCase().includes(term) ||
    p.contact_first_name?.toLowerCase().includes(term) ||
    p.contact_last_name?.toLowerCase().includes(term) ||
    p.job_title?.toLowerCase().includes(term) ||
    p.job_number?.toLowerCase().includes(term) ||
    p.description?.toLowerCase().includes(term) ||
    p.notes?.toLowerCase().includes(term) ||
    p.client_email?.toLowerCase().includes(term) ||
    p.client_phone?.toLowerCase().includes(term) ||
    p.client_street?.toLowerCase().includes(term) ||
    p.client_city?.toLowerCase().includes(term) ||
    p.client_state?.toLowerCase().includes(term) ||
    p.service_address_street?.toLowerCase().includes(term) ||
    p.service_address_city?.toLowerCase().includes(term) ||
    p.created_at?.toLowerCase().includes(term) ||
    p.status?.toLowerCase().includes(term) ||
    p.created_by_name?.toLowerCase().includes(term);

  const filteredProposals = proposals.filter(p => {
    // Search filter — a search with no comma is one phrase (unchanged);
    // with commas, every piece must match somewhere (AND of per-piece OR)
    const searchHasComma = search.includes(',');
    const searchPieces = searchHasComma
      ? search.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
      : [search.toLowerCase()];
    const matchesSearch = !search || searchPieces.every(term => matchesSearchTerm(p, term));
    
    // Status filter
    const matchesStatus = !statusFilter || p.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedProposals = [...filteredProposals].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Handle dates
    if (sortBy === 'created_at' || sortBy === 'updated_at') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    
    // Handle numbers
    if (sortBy === 'total') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    // Handle strings
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Column config for PreferenceTable. Order/labels/widths mirror the
  // previous hard-coded layout so existing users see the same defaults.
  const proposalColumns = [
    { id: '_select', label: '', system: true, defaultWidth: 40,
      render: (p) => (
        ['Accepted', 'Completed', 'Lost'].includes(p.status) ? (
          <input
            type="checkbox"
            className="w-4 h-4 cursor-pointer accent-amber-500"
            checked={selectedIds.has(p.id)}
            onChange={() => toggleSelectProposal(p.id)}
            data-testid={`select-proposal-${p.id}`}
          />
        ) : null
      ) },
    { id: 'proposal_number', label: 'Proposal #', sortKey: 'proposal_number', defaultWidth: 140,
      render: (p) => <span className="font-mono text-sm">{p.proposal_number}</span> },
    { id: 'proposal_type', label: 'Type', sortKey: 'proposal_type', defaultWidth: 130,
      render: (p) => p.proposal_type },
    { id: 'client_name', label: 'Client', sortKey: 'client_name', defaultWidth: 180,
      render: (p) => <span className="font-medium">{p.client_name}</span> },
    { id: 'job_title', label: 'Job Title', sortKey: 'job_title', defaultWidth: 220,
      render: (p) => <span className="text-slate-600">{p.job_title || '-'}</span> },
    { id: 'total', label: 'Total', sortKey: 'total', defaultWidth: 110, alignRight: true,
      render: (p) => <span className="font-medium">${p.total?.toFixed(2)}</span> },
    { id: 'status', label: 'Status', sortKey: 'status', defaultWidth: 130,
      render: (p) => (
        <div className="flex items-center gap-1">
          <Select value={p.status} onValueChange={(v) => handleStatusChange(p, v)}>
            <SelectTrigger className={'w-[130px] h-8 ' + getStatusStyle(p.status)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Accepted">Accepted</SelectItem>
              {(p.status === 'Accepted' || p.status === 'Completed') && (
                <SelectItem value="Completed">Completed</SelectItem>
              )}
              <SelectItem value="Lost">Lost</SelectItem>
            </SelectContent>
          </Select>
          {p.status === 'Lost' && p.lost_reason && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <MessageSquareText className="w-3.5 h-3.5 text-slate-400 cursor-help" data-testid={`lost-reason-icon-${p.id}`} />
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px]" data-testid={`lost-reason-tooltip-${p.id}`}>
                  {p.lost_reason}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ) },
    { id: 'created_at', label: 'Created Date', sortKey: 'created_at', defaultWidth: 140,
      cellClassName: 'text-slate-600 whitespace-nowrap',
      render: (p) => <span data-testid={`proposal-created-${p.id}`}>{p.created_at ? format(new Date(p.created_at), 'MMM d, yyyy') : '-'}</span> },
    { id: 'updated_at', label: 'Date Updated', sortKey: 'updated_at', defaultWidth: 190,
      cellClassName: 'text-slate-600',
      render: (p) => <span data-testid={`proposal-updated-${p.id}`}>{p.updated_at ? format(new Date(p.updated_at), 'MMM d, yyyy h:mm a') : '-'}</span> },
    { id: 'actions', label: 'Actions', defaultWidth: 220,
      render: (p) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/proposals/${p.id}/preview`)} title="Print Preview" data-testid={`print-preview-btn-${p.id}`}><Printer className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-btn-${p.id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDuplicate(p)} title="Duplicate Proposal" data-testid={`duplicate-btn-${p.id}`}><Copy className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="text-amber-600 hover:bg-amber-50" onClick={() => setFollowUpProposal(p)} title="Schedule Follow-up Reminder" data-testid={`followup-btn-${p.id}`}><Bell className="w-4 h-4" /></Button>
          {p.status !== 'Accepted' && p.status !== 'Completed' && (
            <Button variant="ghost" size="sm" className="text-green-600" onClick={() => convertToJob(p.id)} title="Convert to Job" data-testid={`convert-to-job-btn-${p.id}`}><ArrowRight className="w-4 h-4" /></Button>
          )}
          {['Completed', 'Lost'].includes(p.status) && (
            <Button variant="ghost" size="sm" className="text-slate-500 hover:bg-slate-100" onClick={() => handleArchive(p.id)} title="Archive" data-testid={`archive-btn-${p.id}`}><Archive className="w-4 h-4" /></Button>
          )}
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteClick(p)} data-testid={`delete-btn-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ) },
  ];
  const proposalsPrefs = useTablePreferences('proposals', proposalColumns);

  const convertToJob = async (proposalId) => {
    if (!window.confirm('Convert this proposal to a job? This will mark the proposal as Accepted.')) return;
    try {
      const res = await axios.post(`${API}/proposals/${proposalId}/convert-to-job`);
      toast.success(`Job created: ${res.data.job_number}`);
      navigate(`/jobs/${res.data.job_id}`);
    } catch (e) {
      toast.error('Failed to convert proposal');
    }
  };

  // Manual archive for a terminal-status proposal (Completed or Lost) —
  // user-triggered only, no auto-trigger on status change. Accepted is
  // NOT archivable (2026-09-01: must become Lost or Completed first).
  // Status is untouched; this only moves it into the Archive Center.
  // Same window.confirm() pattern as convertToJob above, copied on purpose.
  const handleArchive = async (proposalId) => {
    if (!window.confirm('Archive this proposal? It will move to the Archive Center.')) return;
    try {
      await axios.put(`${API}/proposals/${proposalId}/archive`);
      toast.success('Proposal archived');
      loadProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive proposal');
    }
  };

  // Bulk selection — toggle one row, or clear the whole selection.
  const toggleSelectProposal = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // The bulk action offered depends on the selection's status: all-Accepted
  // can be Marked Completed; all-Completed or all-Lost can be Archived;
  // a mixed-status selection has no valid single action (disabled).
  const selectedProposalsForBulk = proposals.filter(p => selectedIds.has(p.id));
  const selectedStatuses = new Set(selectedProposalsForBulk.map(p => p.status));
  let bulkAction = null;
  if (selectedStatuses.size === 1) {
    const onlyStatus = [...selectedStatuses][0];
    if (onlyStatus === 'Accepted') {
      bulkAction = { type: 'complete', label: `Mark ${selectedIds.size} Completed`, title: 'Mark Proposals Completed', description: `Mark ${selectedIds.size} Accepted proposal(s) as Completed?` };
    } else if (onlyStatus === 'Completed' || onlyStatus === 'Lost') {
      bulkAction = { type: 'archive', label: `Archive ${selectedIds.size}`, title: 'Archive Proposals', description: `Archive ${selectedIds.size} proposal(s)? They'll move to the Archive Center.` };
    }
  }

  const runBulkAction = async () => {
    if (!bulkConfirm) return;
    setBulkProcessing(true);
    try {
      const endpoint = bulkConfirm.type === 'complete' ? '/proposals/bulk/mark-completed' : '/proposals/bulk/archive';
      const res = await axios.post(API + endpoint, { proposal_ids: [...selectedIds] });
      toast.success(`${res.data.updated} updated${res.data.skipped?.length ? `, ${res.data.skipped.length} skipped` : ''}`);
      clearSelection();
      setBulkConfirm(null);
      loadProposals();
    } catch (e) {
      toast.error('Bulk action failed');
    } finally {
      setBulkProcessing(false);
    }
  };

  const openNew = () => {
    setEditData(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditData(p);
    setShowForm(true);
  };

  const saveProposal = async (data, id, keepDialogOpen = false) => {
    setSaving(true);
    try {
      let savedData;
      if (id) {
        const response = await axios.put(API + '/proposals/' + id, data);
        savedData = response.data;
        toast.success('Updated');
      } else {
        const response = await axios.post(API + '/proposals', data);
        savedData = response.data;
        toast.success('Created - you can now add files');
      }
      
      if (keepDialogOpen && savedData) {
        setEditData(savedData);
        loadProposals();
      } else {
        setShowForm(false);
        loadProposals();
      }
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status, reason) => {
    try {
      let url = API + '/proposals/' + id + '/status?status=' + encodeURIComponent(status);
      if (reason) url += '&reason=' + encodeURIComponent(reason);
      await axios.put(url);
      toast.success('Status updated');
      
      if (status === 'Accepted') {
        try {
          const res = await axios.post(`${API}/proposals/${id}/convert-to-job`);
          toast.success(`Job ${res.data.job_number} created from proposal!`);
          navigate(`/jobs/${res.data.job_id}`);
          return;
        } catch (jobError) {
          console.error('Auto-convert to job failed:', jobError);
        }
      }
      
      loadProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  // Marking a proposal Lost pops a small, skippable reason box instead of
  // saving immediately — the actual PUT happens once the user hits Save
  // or Skip in that popup (see handleSkipLostReason/handleSaveLostReason).
  const handleStatusChange = (proposal, newStatus) => {
    if (newStatus === 'Lost') {
      setLostReasonText('');
      setLostReasonTarget(proposal);
    } else {
      updateStatus(proposal.id, newStatus);
    }
  };

  const handleSkipLostReason = () => {
    if (!lostReasonTarget) return;
    updateStatus(lostReasonTarget.id, 'Lost');
    setLostReasonTarget(null);
  };

  const handleSaveLostReason = async () => {
    if (!lostReasonTarget) return;
    setSavingLostReason(true);
    await updateStatus(lostReasonTarget.id, 'Lost', lostReasonText.trim());
    setSavingLostReason(false);
    setLostReasonTarget(null);
  };

  const handleDeleteClick = (proposal) => {
    setProposalToDelete(proposal);
    setShowDeleteDialog(true);
  };


  const confirmDelete = async () => {
    if (!proposalToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(API + '/proposals/' + proposalToDelete.id);
      toast.success('Proposal deleted');
      loadProposals();
      setShowDeleteDialog(false);
      setProposalToDelete(null);
    } catch (e) {
      toast.error('Failed to delete proposal');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = (proposal) => {
    const duplicated = {
      ...proposal,
      status: 'Draft',
      notes: proposal.notes || '',
    };
    delete duplicated.id;
    delete duplicated.proposal_number;
    delete duplicated.created_at;
    delete duplicated.updated_at;
    delete duplicated.attachments;
    setEditData(null);
    setShowForm(true);
    setTimeout(() => {
      setEditData({ ...duplicated, _isDuplicate: true });
    }, 50);
    toast.info('Proposal duplicated as new Draft — review and save');
  };

  const downloadPDF = async (proposal) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/proposals/${proposal.id}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Proposal_${proposal.proposal_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF download error:', e);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return <div className="h-64 bg-slate-200 animate-pulse rounded-lg" data-testid="proposals-loading" />;
  }

  return (
    <div className="space-y-6" data-testid="proposals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Quotes & Proposals
          </h1>
          <p className="text-slate-500 mt-1">Create and manage client proposals</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
              data-testid="view-toggle-table"
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
              data-testid="view-toggle-cards"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {canCreateProposals && (
            <Button size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" onClick={openNew} data-testid="new-proposal-btn">
              <Plus className="w-4 h-4 mr-2" />New Proposal
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search proposals..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-50"
                data-testid="proposal-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-slate-50 px-3 py-2 text-sm"
              data-testid="proposal-status-filter"
            >
              <option value="">All Statuses</option>
              {PROPOSAL_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {PROPOSAL_STATUSES.map(status => {
          const matching = proposals.filter(p => p.status === status.value);
          const count = matching.length;
          const totalValue = matching.reduce((sum, p) => sum + (p.total || 0), 0);
          return (
            <Card 
              key={status.value} 
              className={`border-slate-200 cursor-pointer hover:border-amber-500/50 transition-all ${statusFilter === status.value ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status.value ? '' : status.value)}
              data-testid={`status-card-${status.value.toLowerCase()}`}
            >
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-slate-900">{count}</div>
                <div className="text-xs text-slate-500">{status.label}</div>
                {totalValue > 0 && (
                  <div className="text-xs text-green-600 font-medium mt-1">
                    ${totalValue.toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bulk Selection Toolbar — appears once at least one Accepted/
          Completed/Lost row is checked. Action offered matches the
          selection's status; a mixed selection just shows Clear. */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="proposals-bulk-toolbar">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <span className="font-medium">{selectedIds.size} selected</span>
            <button className="text-slate-500 hover:text-slate-700 underline text-xs" onClick={clearSelection} data-testid="bulk-clear-btn">Clear</button>
          </div>
          {bulkAction ? (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium" onClick={() => setBulkConfirm(bulkAction)} data-testid={`bulk-${bulkAction.type}-btn`}>
              {bulkAction.label}
            </Button>
          ) : (
            <span className="text-xs text-slate-500 italic">Select rows with the same status to run a bulk action</span>
          )}
        </div>
      )}

      {/* Proposals List */}
      {sortedProposals.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No proposals found</h3>
            <p className="text-slate-500">
              {search || statusFilter ? 'Try adjusting your filters' : 'Create your first proposal to get started'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View — resize, reorder, hide columns are per-user prefs (Phase 1) */
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <PreferenceTable
              columns={proposalColumns}
              rows={sortedProposals}
              rowKey={(p) => p.id}
              prefs={proposalsPrefs}
              sortBy={sortBy}
              sortDir={sortOrder}
              onSort={(col) => handleSort(col.sortKey)}
              emptyMessage="No proposals match your filters."
              data-testid="proposals-table"
            />
          </CardContent>
        </Card>
      ) : (
        /* Card/Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedProposals.map(p => (
            <Card key={p.id} className="border-slate-200 hover:border-amber-500/50 transition-all" data-testid={`proposal-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono text-sm text-slate-500">{p.proposal_number}</div>
                    <div className="font-semibold text-slate-900">{p.client_name}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(p.status)}`} data-testid={`proposal-card-status-${p.id}`}>
                      {getStatusLabel(p.status)}
                    </span>
                    {p.status === 'Lost' && p.lost_reason && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <MessageSquareText className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[240px]">{p.lost_reason}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <FileText className="w-4 h-4" />
                    <span>{p.proposal_type}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-semibold text-slate-900">${p.total?.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400">{format(new Date(p.created_at), 'MMM d, yyyy')}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/proposals/${p.id}/preview`)} title="Print Preview"><Printer className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(p)} title="Duplicate Proposal" data-testid={`duplicate-card-btn-${p.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-amber-600 hover:bg-amber-50" onClick={() => setFollowUpProposal(p)} title="Schedule Follow-up Reminder" data-testid={`followup-card-btn-${p.id}`}><Bell className="w-4 h-4" /></Button>
                    {!['Accepted', 'Completed', 'Lost'].includes(p.status) && (
                      <Button variant="ghost" size="sm" className="text-green-600" onClick={() => convertToJob(p.id)} title="Convert to Job" data-testid={`convert-to-job-card-btn-${p.id}`}><ArrowRight className="w-4 h-4" /></Button>
                    )}
                    {['Completed', 'Lost'].includes(p.status) && (
                      <Button variant="ghost" size="sm" className="text-slate-500 hover:bg-slate-100" onClick={() => handleArchive(p.id)} title="Archive" data-testid={`archive-card-btn-${p.id}`}><Archive className="w-4 h-4" /></Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteClick(p)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ProposalFormDialog open={showForm} onOpenChange={setShowForm} onSave={saveProposal} saving={saving} contacts={contacts} editData={editData} />
      <ProposalPreviewDialog open={showPreview} onOpenChange={setShowPreview} proposal={previewData} onRefresh={refreshPreviewData} />

      <ProposalFollowUpDialog
        open={!!followUpProposal}
        onOpenChange={(v) => { if (!v) setFollowUpProposal(null); }}
        proposal={followUpProposal}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete proposal <strong>{proposalToDelete?.proposal_number}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Confirmation Dialog — shown before POSTing either
          bulk/mark-completed or bulk/archive. */}
      <AlertDialog open={!!bulkConfirm} onOpenChange={(v) => { if (!v) setBulkConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{bulkConfirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkAction}
              disabled={bulkProcessing}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid="bulk-confirm-btn"
            >
              {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lost Reason popup — lightweight, skippable text box shown when a
          proposal is marked Lost. Not a form; just Save or Skip. */}
      <Dialog open={!!lostReasonTarget} onOpenChange={(v) => { if (!v) setLostReasonTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle>Mark {lostReasonTarget?.proposal_number} as Lost</DialogTitle>
            <DialogDescription>
              Optional — jot down why, e.g. &ldquo;lost to competitor on price.&rdquo; Skip if you&apos;d rather not.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason (optional)"
            value={lostReasonText}
            onChange={(e) => setLostReasonText(e.target.value)}
            rows={3}
            data-testid="lost-reason-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={handleSkipLostReason} disabled={savingLostReason} data-testid="lost-reason-skip-btn">
              Skip
            </Button>
            <Button onClick={handleSaveLostReason} disabled={savingLostReason} className="bg-red-600 hover:bg-red-700 text-white" data-testid="lost-reason-save-btn">
              {savingLostReason ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
